import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ReactComponent as Delete } from '$icons/TrashIcon.svg';
import {
  GetOneSmartInterviewOutput,
  smartInterviewsApi,
  useCreateSmartInterviewMutation,
  useLazyGetOneSmartInterviewQuery,
  useUpdateSmartInterviewMutation,
} from '$store/modules/smart-interviews';
import { errorHandler, successHandler } from '$store/utils';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';

const SmartInterviewEditor = ({
  smartInterviewData,
  onCancel,
  onCreateOrUpdate,
  action,
}: {
  smartInterviewData: GetOneSmartInterviewOutput;
  onCancel: () => void;
  onCreateOrUpdate: (newInterview: GetOneSmartInterviewOutput) => void;
  action: 'new' | 'update';
}) => {
  const dispatch = useAppDispatch();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [selectedAudio, setSelectedAudio] = useState<{
    activeAudio: string;
    audio: string;
  }>({ activeAudio: '', audio: '' });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    formState: { isDirty, errors },
  } = useForm<GetOneSmartInterviewOutput>({
    defaultValues: {
      ...smartInterviewData,
    },
  });

  const WelcomeMessages = watch('WelcomeMessages');
  const Questions = watch('Questions');

  const [createSmartInterview, { isLoading: isLoadingCreate }] =
    useCreateSmartInterviewMutation();
  const [updateSmartInterview, { isLoading: isLoadingUpdate }] =
    useUpdateSmartInterviewMutation();
  const [getOneSmartInterview] = useLazyGetOneSmartInterviewQuery();

  const handleUpdateSmartInterview = async (
    smartInterviewId: number,
    data: GetOneSmartInterviewOutput
  ) => {
    await updateSmartInterview({
      smartInterviewId,
      data,
    }).then(async (resp) => {
      if ('data' in resp) {
        await getOneSmartInterview({ smartInterviewId }).then(
          (oneSmartInterviewResponse) => {
            successHandler('Smart Interview updated successfully', true, false);
            onCreateOrUpdate(
              oneSmartInterviewResponse.data as GetOneSmartInterviewOutput
            );
            reset(oneSmartInterviewResponse.data);
            dispatch(
              smartInterviewsApi.util.invalidateTags(['SmartInterviews'])
            );
          }
        );
      } else {
        errorHandler(
          '[UPDATE SMART INTERVIEW]',
          'Something went wrong. Please try again later',
          true,
          false
        );
      }
    });
  };

  const handleCreateSmartInterview = async (
    formData: GetOneSmartInterviewOutput
  ) => {
    await createSmartInterview(formData).then(async (resp) => {
      if ('data' in resp) {
        await getOneSmartInterview({
          smartInterviewId: resp.data.Id as number,
        }).then((oneSmartInterviewResponse) => {
          successHandler('Smart Interview created successfully', true, false);
          onCreateOrUpdate(
            oneSmartInterviewResponse.data as GetOneSmartInterviewOutput
          );
          reset(oneSmartInterviewResponse.data);
          dispatch(smartInterviewsApi.util.invalidateTags(['SmartInterviews']));
        });
      } else {
        errorHandler(
          '[CREATE SMART INTERVIEW]',
          'Something went wrong. Please try again later',
          true,
          false
        );
      }
    });
  };

  const formSubmitHandler = async (formData: GetOneSmartInterviewOutput) => {
    const data = {
      Name: formData.Name,
      Description: formData.Description,
      WelcomeMessages: formData.WelcomeMessages,
      Questions: formData.Questions,
    };

    if (action === 'update') {
      handleUpdateSmartInterview(formData.Id as number, data);
    } else {
      handleCreateSmartInterview(data);
    }
  };

  useEffect(() => {
    reset(smartInterviewData);
  }, [reset, smartInterviewData]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
      if (selectedAudio.audio) audioRef.current.play();
    }
  }, [selectedAudio]);

  return (
    <form
      onSubmit={handleSubmit(formSubmitHandler)}
      className="mt-4 relative"
      data-testid="smartInterviewEditor"
    >
      <audio
        ref={audioRef}
        onEnded={() => setSelectedAudio({ audio: '', activeAudio: '' })}
        data-testid="smartInterviewAudioPlayer"
      >
        <source src={selectedAudio.audio} />
        <track kind="captions" />
      </audio>
      <fieldset
        disabled={isLoadingCreate || isLoadingUpdate}
        className="flex flex-col gap-8"
      >
        <label>
          <h3 className="mb-2 text-xl font-semibold">Name</h3>
          <input
            autoComplete="off"
            defaultValue={smartInterviewData.Name}
            {...register('Name', {
              required: 'Name is required',
            })}
            className="w-full border border-solid border-gray-200 rounded outline-none py-1 px-2"
            placeholder="Type a name for this Smart Interview"
          />
          <span>{errors?.Name?.message}</span>
        </label>
        <label>
          <h3 className="mb-2 text-xl font-semibold">Description</h3>
          <input
            autoComplete="off"
            defaultValue={smartInterviewData.Description}
            {...register('Description')}
            className="w-full border border-solid border-gray-200 rounded outline-none py-1 px-2"
            placeholder="Type a description for this Smart Interview"
          />
        </label>
        <div>
          <h3 className="mb-2 text-xl font-semibold">Welcome Messages</h3>
          <div className="flex flex-col w-full gap-y-2">
            {WelcomeMessages.map((singleWelcomeMessage, index) => {
              const audioName = `welcomeMessageAudio_${index}`;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`welcomeMessage_${index}`}>
                  <div className="flex flex-row">
                    {smartInterviewData?.WelcomeAudio?.[index] && (
                      <button
                        data-testid="smartInterviewWelcomeAudioBtn"
                        className="mr-2"
                        type="button"
                        aria-label={`${
                          selectedAudio.activeAudio === audioName
                            ? 'Stop Audio'
                            : 'Play Audio'
                        }`}
                        onClick={() => {
                          if (selectedAudio.activeAudio === audioName)
                            setSelectedAudio({
                              audio: '',
                              activeAudio: '',
                            });
                          else
                            setSelectedAudio({
                              audio: `data:audio/mp3;base64,${smartInterviewData?.WelcomeAudio?.[index]}`,
                              activeAudio: audioName,
                            });
                        }}
                      >
                        <i
                          className={`pi ${
                            selectedAudio.activeAudio === audioName
                              ? 'pi-stop'
                              : 'pi-play'
                          }`}
                        />
                      </button>
                    )}
                    <input
                      autoComplete="off"
                      defaultValue={singleWelcomeMessage}
                      className={`w-full border border-solid border-gray-200 rounded rounded-r-none outline-none py-1 px-4 ${
                        errors.WelcomeMessages &&
                        errors.WelcomeMessages[index] &&
                        '!border-red-700'
                      }`}
                      {...register(`WelcomeMessages.${index}`, {
                        required: `Field can't be empty. Please type a Welcome Message or delete this field`,
                      })}
                      placeholder="Type a welcome message for this Smart Interview"
                    />
                    <button
                      className={`rounded rounded-l-none border border-l-0 border-gray-200 border-solid px-4 hover:bg-red-400 group ${
                        errors.WelcomeMessages &&
                        errors.WelcomeMessages[index] &&
                        '!border-red-700'
                      }`}
                      type="button"
                      onClick={() =>
                        setValue(
                          'WelcomeMessages',
                          WelcomeMessages.filter(
                            (textQuestion) =>
                              textQuestion !== singleWelcomeMessage
                          ),
                          { shouldDirty: true }
                        )
                      }
                    >
                      <Delete className="group-hover:fill-white" />
                    </button>
                  </div>
                  {errors.WelcomeMessages && errors.WelcomeMessages[index] && (
                    <span className="text-xs text-red-700">
                      *{errors!.WelcomeMessages[index]!.message}
                    </span>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              className="rounded-full self-center border border-internal-main border-solid px-4 py-1 text-sm text-internal-main disabled:opacity-25 disabled:cursor-not-allowed"
              disabled={
                !!errors.WelcomeMessages || WelcomeMessages.indexOf('') !== -1
              }
              onClick={() =>
                setValue(`WelcomeMessages.${WelcomeMessages.length}`, '')
              }
            >
              Add a new Welcome Message
            </button>
          </div>
        </div>
        <div>
          <h3 className="mb-2 text-xl font-semibold">Questions</h3>
          <div className="flex flex-col w-full gap-y-4">
            {Questions.map((singleQuestion, index) => {
              const audioName = `questionAudio_${index}`;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`questions_${index}`}>
                  <div className="flex flex-row">
                    {smartInterviewData?.QuestionsAudio?.[index] && (
                      <button
                        data-testid="smartInterviewQuestionAudioBtn"
                        type="button"
                        className="mr-2"
                        aria-label={`${
                          selectedAudio.activeAudio === audioName
                            ? 'Stop Audio'
                            : 'Play Audio'
                        }`}
                        onClick={() => {
                          if (selectedAudio.activeAudio === audioName)
                            setSelectedAudio({
                              audio: '',
                              activeAudio: '',
                            });
                          else
                            setSelectedAudio({
                              audio: `data:audio/mp3;base64,${smartInterviewData?.QuestionsAudio?.[index]}`,
                              activeAudio: audioName,
                            });
                        }}
                      >
                        <i
                          className={`pi ${
                            selectedAudio.activeAudio === audioName
                              ? 'pi-stop'
                              : 'pi-play'
                          }`}
                        />
                      </button>
                    )}
                    <input
                      autoComplete="off"
                      defaultValue={singleQuestion}
                      className={`w-full border border-solid border-gray-200 rounded rounded-r-none outline-none py-1 px-4 ${
                        errors.Questions &&
                        errors.Questions[index] &&
                        '!border-red-700'
                      }`}
                      {...register(`Questions.${index}`, {
                        required: `Field can't be empty. Please type a Question or delete this field`,
                      })}
                      placeholder="Type a question for this Smart Interview"
                    />
                    <button
                      className={`rounded rounded-l-none border border-l-0 border-gray-200 border-solid px-4 hover:bg-red-400 group ${
                        errors.Questions &&
                        errors.Questions[index] &&
                        '!border-red-700'
                      }`}
                      type="button"
                      onClick={() =>
                        setValue(
                          'Questions',
                          Questions.filter(
                            (textQuestion) => textQuestion !== singleQuestion
                          ),
                          { shouldDirty: true }
                        )
                      }
                    >
                      <Delete className="group-hover:fill-white" />
                    </button>
                  </div>
                  {errors.Questions && errors.Questions[index] && (
                    <span className="text-xs text-red-700">
                      *{errors!.Questions[index]!.message}
                    </span>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              className="rounded-full self-center border border-internal-main border-solid px-4 py-1 text-sm text-internal-main disabled:opacity-25 disabled:cursor-not-allowed"
              onClick={() => setValue(`Questions.${Questions.length}`, '')}
              disabled={!!errors.Questions || Questions.indexOf('') !== -1}
            >
              Add a new Question
            </button>
          </div>
        </div>
        <div className="flex flex-row justify-end gap-4 mt-4">
          <button
            type="submit"
            className="rounded px-2 py-1 bg-red-500 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-25"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded px-2 py-1 bg-internal-main font-semibold text-white disabled:cursor-not-allowed disabled:opacity-25"
            disabled={!isDirty}
          >
            {!(isLoadingCreate || isLoadingUpdate) &&
              (action === 'new' ? 'Submit' : 'Update')}
            {(isLoadingCreate || isLoadingUpdate) && 'Saving...'}
          </button>
        </div>
      </fieldset>
    </form>
  );
};

export default SmartInterviewEditor;
